//APP.JS
//Init of the react app. Call be index.js

//External imports
import { CSSProperties, Suspense, useState } from "react"
import { Route, Navigate, Routes, BrowserRouter } from "react-router-dom"
import axios, { AxiosInstance } from "axios"
import cookie from "react-cookies"
import { ToastContainer } from "react-toastify"
import EstablishmentImportTasks from "@/entities/establishment/ui/EstablishmentImportTasks"
import { useIdleTimer } from "react-idle-timer"
import { FirebaseApp, initializeApp } from "firebase/app"
import { Auth, getAuth, GoogleAuthProvider } from "firebase/auth"

//Intenal imports
import env from "@/env"
import "@/utils/animation-mode.utils" //Suit la préférence « réduire les animations » du système
import logout from "@/utils/logout.utils"
import { reportingExpiresSoon } from "@/utils/reporting-token.utils"

//IDTREE widgets
import ErrorMessage from "@/components/error-message"
import MobileWarningModal from "@/modals/mobile-warning.modal"

//IDTree Routes
import LoginApp from "@/routes/login-app.route"
import Onboarding from "@/routes/onboarding.route"
import SupervisorAccounts from "@/routes/supervisor-accounts.route"
import SupervisorDemoRoute from "@/routes/supervisor-demo.route"
import SupervisorAccount from "@/routes/supervisor-account.route"
import SupervisorLms from "@/routes/supervisor-lms.route"
import SurveyTemplate from "@/routes/survey-template.route"
import SurveyTemplates from "@/routes/survey-templates.route"
import LoginPublic from "@/routes/login-public.route"
import LoginMail from "@/routes/login-mail.route"
import LoginCode from "@/routes/login-code.route"
import LoginRegister from "@/routes/login-register.route"
import DashboardRps from "@/routes/dashboard-rps.route"
import SuperadminUsers from "@/routes/superadmin-users.route"
import LoginSupervisor from "@/routes/login-supervisor.route"
import SuperadminSettings from "@/routes/superadmin-settings.route"
import OnboardingVideo from "@/routes/onboarding-video.route"
import OnboardingSignup from "@/routes/onboarding-signup.route"
import DashboardNpsRoute from "@/routes/dashboard-nps.route"
import ReportEditRoute from "@/routes/report-edit.route"
import ReportListRoute from "@/routes/report-list.route"
import HomeRoute from "@/routes/home.route"
import ProjectListRoute from "@/routes/project-list.route"
import ProjectEditRoute from "@/routes/project-edit.route"
import AdmImportManualRoute from "@/routes/adm-import-manual.route"
import AdmImportFileRoute from "@/routes/adm-import-file.route"
import AdmImportMappingRoute from "@/routes/adm-import-mapping.route"
import AdmRolesRoute from "@/routes/adm-roles.route"
import AdmRestrictedAccessRoute from "@/routes/adm-restricted-access.route"
import LoginSsoAuthRoute from "@/routes/login-sso-auth.route"
import TemplateEditorRoute from "@/routes/template-editor.route"
import DashboardHeatmapRoute from "@/routes/dashboard-heatmap.route"
import DashboardCompareRoute from "@/routes/dashboard-compare.route"
import AdmSelfRoute from "@/routes/adm-self.route"
import AdmSettingsRoute from "@/routes/adm-settings.route"
import AdmUsersRoute from "@/routes/adm-users.route"
import DashboardHomeRoute from "@/routes/dashboard-home.route"
import DashboardRepartitionRoute from "@/routes/dashboard-repartition.route"
import DashboardMessagesRoute from "@/routes/dashboard-messages.route"
import DashboardOverviewRoute from "@/routes/dashboard-overview.route"
import DashboardQuestionsRoute from "@/routes/dashboard-questions.route"
import DashboardParticipationRoute from "@/routes/dashboard-participation.route"
import SuperadminTemplatesRoute from "@/routes/superadmin-templates.route"
import ProjectInvitationsRoute from "@/routes/project-invitations.route"
import TasksHomeRoute from "@/routes/tasks-home.route"
import TasksEditRoute from "@/routes/tasks-edit.route"
import DashboardHistoryRoute from "@/routes/dashboard-history.route"
import SupervisorFeedbacksRoute from "@/routes/supervisor-feedbacks.route"
import LoginSsoRedirectionRoute from "@/routes/login-sso-redirection.route"
import CommissionsRoute from "@/routes/commissions.route"
import SupervisorTemplatesRoute from "@/routes/supervisor-templates.route"
import SupervisorReportsRoute from "@/routes/supervisor-reports.route"
import DashboardPopulationsRoute from "@/routes/dashboard-populations.route"
import SupervisorBenchmarkRoute from "@/routes/supervisor-benchmark.route"
import UpgradeRoute from "@/routes/upgrade.route"
import LearningRoute from "@/routes/learning.route"
import RisksUpdatesActionsRoute from "./routes/risks-updates-actions.route"
import ChatWidget from "./widgets/chat.widget"
import RisksEstablishmentsRoute from "./routes/establishments.route"
import RisksAdminsRoute from "./routes/risks-admins.route"
import TasksSelfRoute from "./routes/tasks-self.route"
import TasksWizardRoute from "./routes/tasks-wizard.route"
import RisksEstablishmentRoute from "./routes/establishment.route"
import RisksScalesRoute from "./routes/risks-scales.route"
import RisksSitesRoute from "./routes/risks-sites.route"
import TabletSetupModal from "./components/TabletSetupModal"
import RisksPreventionActions from "./routes/risks-prevention-actions.route"
import RisksCalendarRoute from "./routes/risks-calendar.route"
import RisksDocumentRoute from "./routes/risks-documents.route"
import RisksPopulationsRoute from "./routes/risks-populations.route"
import RisksPopulationRoute from "./routes/risks-population.route"
import RouteResetter from "./components/RouteResetter"
import RisksFamiliesRoute from "./routes/risks-families.route"
import RisksRpsRoute from "./routes/risks-rps.route"
import RisksFamilyRoute from "./routes/risks-family.route"
import TasksAllRoute from "./routes/tasks-all.route"
import RisksDocumentDetailRoute, { DocumentHome } from "./routes/risks-document.route"

/* INIT APP */

// Cookie options (sécurité)
const isLocalhost = window.location.hostname === "localhost"
const COOKIE_OPTIONS = {
  path: "/",
  secure: !isLocalhost,
  sameSite: "lax" as const,
  expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30)
}

//Init firebase (auth)
const firebaseApp: FirebaseApp = initializeApp(env.REACT_APP_FIREBASE)
export const firebaseAppAuth: Auth = getAuth(firebaseApp)
export const firebaseProvider: GoogleAuthProvider = new GoogleAuthProvider()

//Init axios (API Rest IDTree)
export const instanceBackend: AxiosInstance = axios.create({
  baseURL: env.REACT_APP_URL_BACKEND,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json"
  }
})

//App for qrcode invitations and create survey surveys
export const instanceQrcode: AxiosInstance = axios.create({
  baseURL: env.REACT_APP_URL_QRCODE,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json"
  }
})

//Init axios (reporting service)
export const instanceReporting: AxiosInstance = axios.create({
  baseURL: env.REACT_APP_URL_REPORTING,
  timeout: 40000,
  headers: {
    "Content-Type": "application/json"
  }
})

//Init firebase token
if (!cookie.load("firebaseToken")) {
  cookie.save("firebaseToken", "null", COOKIE_OPTIONS)
  instanceBackend.defaults.headers.common["firebaseToken"] = "null"
} else {
  instanceBackend.defaults.headers.common["firebaseToken"] = cookie.load("firebaseToken")
}

//Init idtree token
if (!cookie.load("idtreeToken")) {
  cookie.save("idtreeToken", "null", COOKIE_OPTIONS)
  instanceBackend.defaults.headers.common["idtreeToken"] = "null"
  instanceQrcode.defaults.headers.common["idtreeToken"] = "null"
  instanceReporting.defaults.headers.common["idtreeToken"] = "null"
} else {
  instanceBackend.defaults.headers.common["idtreeToken"] = cookie.load("idtreeToken")
  instanceQrcode.defaults.headers.common["idtreeToken"] = cookie.load("idtreeToken")
  instanceReporting.defaults.headers.common["idtreeToken"] = cookie.load("idtreeToken")
}

//Init reporting token
if (!cookie.load("reportingToken")) {
  cookie.save("reportingToken", "null", COOKIE_OPTIONS)
  instanceReporting.defaults.headers.common["reportingToken"] = "null"
} else {
  instanceReporting.defaults.headers.common["reportingToken"] = cookie.load("reportingToken")
}

//Silent refresh interceptor
//Un seul refresh en vol : les requetes 401 concurrentes attendent le meme.
let refreshPromise: Promise<string> | null = null

const runRefresh = async (): Promise<string> => {
  const refreshToken = cookie.load("idtreeRefreshToken")
  if (!refreshToken || refreshToken === "null") {
    throw new Error("refresh_token_missing")
  }

  //Le backend relit le jeton courant pour retrouver le compte client visite par un superviseur :
  //sans lui, le refresh renvoie une session superviseur nue et ejecte du compte.
  //Jeton de cet onglet, pas le cookie partage entre onglets.
  const { data } = await axios.post(env.REACT_APP_URL_BACKEND + "/_auth/refresh", {
    refreshToken,
    idtreeToken: instanceBackend.defaults.headers.common["idtreeToken"]
  })

  if (data.error || !data.token) {
    throw new Error(data.error || "Unable to refresh access token")
  }

  let token = data.token

  const establishmentId = cookie.load("idtreeEstablishmentId")
  if (establishmentId && establishmentId !== "null") {
    try {
      const establishmentResponse = await axios.post(
        env.REACT_APP_URL_BACKEND + "/establishment/connect",
        { establishmentId },
        { headers: { idtreeToken: token } }
      )

      if (establishmentResponse.data && typeof establishmentResponse.data === "string") {
        token = establishmentResponse.data
      } else {
        cookie.remove("idtreeEstablishmentId", { path: "/" })
      }
    } catch {
      cookie.remove("idtreeEstablishmentId", { path: "/" })
    }
  }

  //Update access token everywhere
  cookie.save("idtreeToken", token, COOKIE_OPTIONS)
  if (data.refreshToken) {
    cookie.save("idtreeRefreshToken", data.refreshToken, COOKIE_OPTIONS)
  }
  instanceBackend.defaults.headers.common["idtreeToken"] = token
  instanceQrcode.defaults.headers.common["idtreeToken"] = token
  instanceReporting.defaults.headers.common["idtreeToken"] = token

  //Re-auth reporting service
  //Le token reporting embarque l'idtreeToken : il doit etre refait a chaque refresh.
  //axios nu, pas instanceReporting : cette requete passerait sinon par les intercepteurs de refresh
  //et attendrait un renouvellement dont elle fait partie.
  try {
    const reportingResponse = await axios.get(instanceReporting.defaults.baseURL + "/auth", {
      headers: { idtreeToken: token }, timeout: 15000
    })
    //Un echec garde le jeton precedent : il ne doit pas deconnecter.
    if (reportingResponse.data && typeof reportingResponse.data === "string") {
      cookie.save("reportingToken", reportingResponse.data, COOKIE_OPTIONS)
      instanceReporting.defaults.headers.common["reportingToken"] = reportingResponse.data
    }
  } catch {
    /* Reporting injoignable : on garde le token precedent, la session app reste ouverte. */
  }

  return token
}

const getRefreshPromise = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = runRefresh().catch(error => {
      logout(true)
      throw error
    }).finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

// Token a renewal already started from: still in place means the reporting re-auth failed, the 401 path takes over.
let renewedFrom: unknown = Symbol("no reporting renewal yet")

// Renew before reporting calls: internal access tokens now expire after 15 minutes.
instanceReporting.interceptors.request.use(async config => {
  if (config.url?.split("?")[0].endsWith("/auth") || (config as any)._retry) return config
  const reportingToken = instanceReporting.defaults.headers.common["reportingToken"]
  if (refreshPromise || (reportingToken !== renewedFrom && reportingExpiresSoon(reportingToken))) {
    renewedFrom = reportingToken
    await getRefreshPromise()
    // This request has already had its one renewal attempt.
    ;(config as any)._retry = true
    config.headers.set("idtreeToken", instanceReporting.defaults.headers.common["idtreeToken"])
    config.headers.set("reportingToken", instanceReporting.defaults.headers.common["reportingToken"])
  }
  return config
})

const refreshSession = (instance: AxiosInstance) => async (error: any) => {
  const originalRequest = error.config
  //Le backend distingue les deux cas : 401 quand le jeton est expire (TokenExpiredError), 403
  //quand il n'est pas valide pour la route appelee - typiquement un jeton d'application sur une
  //URL /supervisor/ ou l'inverse. Renouveler puis deconnecter sur un 403 detruisait la session en
  //cours pour une simple requete envoyee avec le mauvais jeton.
  if (
    error.response?.status !== 401 ||
    !originalRequest ||
    originalRequest._retry ||
    originalRequest.url?.startsWith("/_auth/")
  ) {
    return Promise.reject(error)
  }

  originalRequest._retry = true

  originalRequest.headers = originalRequest.headers ?? {}

  // A late 401 can belong to a request sent before another request renewed the session.
  const isReportingData = instance === instanceReporting && !originalRequest.url?.split("?")[0].endsWith("/auth")
  const header = isReportingData ? "reportingToken" : "idtreeToken"
  const current = instance.defaults.headers.common[header]
  const sent = originalRequest.headers.get ? originalRequest.headers.get(header) : originalRequest.headers[header]
  const alreadyRenewed = typeof current === "string" && current !== "null" && current !== sent

  let token: string
  try {
    token = alreadyRenewed ? (instanceBackend.defaults.headers.common["idtreeToken"] as string) : await getRefreshPromise()
  } catch {
    //On rejette l'erreur axios d'origine plutot qu'une Error nue : cette derniere n'a pas de
    //.response, et le middleware la prendrait pour un timeout reseau ("middleware_error_timeout").
    return Promise.reject(error)
  }

  originalRequest.headers["idtreeToken"] = token
  //Le rejeu backend ne porte pas de reportingToken : le backend n'autorise pas cet en-tete en CORS,
  //le preflight du rejeu echouerait.
  if (instance === instanceReporting) {
    originalRequest.headers["reportingToken"] = instanceReporting.defaults.headers.common["reportingToken"]
  }
  return instance(originalRequest)
}
instanceBackend.interceptors.response.use(response => response, refreshSession(instanceBackend))
//Le reporting expire de son cote (session interne revalidee) : il doit declencher le meme refresh.
instanceReporting.interceptors.response.use(response => response, refreshSession(instanceReporting))

//App React
//Pages risques du document unique : les memes que dans l'app, en lecture seule (cf. RisksSpace).
const documentRiskRoutes = (
  <>
    <Route index element={<DocumentHome />} />
    <Route path="phy" element={<RisksFamiliesRoute />} />
    <Route path="phy/:familyId" element={<RisksFamilyRoute />} />
    <Route path="rps" element={<RisksRpsRoute />} />
    <Route path="populations" element={<RisksPopulationsRoute />} />
    <Route path="populations/:populationId" element={<RisksPopulationRoute />} />
    <Route path="sites" element={<RisksSitesRoute />} />
    <Route path="sites/:siteId" element={<RisksSitesRoute />} />
  </>
)

function App() {
  const [scanPanelWidth, setScanPanelWidth] = useState(0)
  //Detect if mobile (trigger display error message)
  const isMobile: boolean = false

  //If inactivity
  //Redirection to login page
  const handleOnIdle = () => {
    if (window.location.pathname.indexOf("/login/app") === -1) {
      logout(true)
    }
  }

  //Get IdleTimer (30 minutes)
  useIdleTimer({
    timeout: 1000 * 60 * 60 * 12,
    onIdle: handleOnIdle,
    debounce: 500
  })

  //Title of the page
  document.title = "IDTree"

  return (
    <Suspense fallback="loading">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <RouteResetter />
        {/* 🧩 Synchronisation des contextes ADM / Risks 
        <AppContextLoader />*/}

        <div
          className="flex dark-t notranslate"
          translate="no"
          style={{ "--app-content-width": `calc(100vw - ${scanPanelWidth}px)` } as CSSProperties}>
          {isMobile && <MobileWarningModal />}

          <ChatWidget />

          <ErrorMessage />

          <ToastContainer
            autoClose={8000}
            position="bottom-right"
            hideProgressBar={true}
            theme="colored"
            pauseOnHover></ToastContainer>

          <EstablishmentImportTasks onPanelWidthChange={setScanPanelWidth} />

          <Routes>
            {/** home page */}
            <Route path="/home" element={<HomeRoute />} />

            <Route path="/adm/import-file" element={<AdmImportFileRoute />} />
            <Route path="/adm/import-manual" element={<AdmImportManualRoute />} />
            <Route path="/adm/import-mapping" element={<AdmImportMappingRoute />} />
            <Route path="/adm/roles" element={<AdmRolesRoute />} />
            <Route path="/adm/restricted-access/:roleId?" element={<AdmRestrictedAccessRoute />} />
            <Route path="/adm/self" element={<AdmSelfRoute />} />
            <Route path="/adm/settings" element={<AdmSettingsRoute />} />
            <Route path="/adm/users" element={<AdmUsersRoute />} />

            <Route path="/commissions" element={<CommissionsRoute />} />

            <Route path="/dashboard/compare" element={<DashboardCompareRoute />} />
            <Route path="/dashboard/heatmap" element={<DashboardHeatmapRoute />} />
            <Route path="/dashboard/history" element={<DashboardHistoryRoute />} />
            <Route path="/dashboard/home" element={<DashboardHomeRoute />} />
            <Route path="/dashboard/repartition" element={<DashboardRepartitionRoute />} />
            <Route path="/dashboard/messages" element={<DashboardMessagesRoute />} />
            <Route path="/dashboard/overview" element={<DashboardOverviewRoute />} />
            <Route path="/dashboard/nps" element={<DashboardNpsRoute />} />
            <Route path="/dashboard/participation" element={<DashboardParticipationRoute />} />
            <Route path="/dashboard/populations" element={<DashboardPopulationsRoute />} />
            <Route path="/dashboard/questions" element={<DashboardQuestionsRoute />} />
            <Route path="/dashboard/rps" element={<DashboardRps />} />

            <Route path="/risks/admins" element={<RisksAdminsRoute />} />
            <Route
              path="/risks/prevention-actions"
              element={<Navigate replace to="/risks/prevention-actions/in-place" />}
            />
            <Route path="/risks/prevention-actions/in-place" element={<RisksPreventionActions view="in-place" />} />
            <Route path="/risks/prevention-actions/planned" element={<RisksPreventionActions view="planned" />} />
            <Route path="/risks/prevention-actions/to-plan" element={<RisksPreventionActions view="to-plan" />} />
            <Route path="/risks/prevention-actions/overdue" element={<RisksPreventionActions view="overdue" />} />
            <Route path="/risks/prevention-actions/missing" element={<RisksPreventionActions view="missing" />} />
            <Route path="/risks/calendar" element={<RisksCalendarRoute />} />
            <Route path="/risks/establishments" element={<RisksEstablishmentsRoute />} />
            <Route path="/risks/establishment" element={<RisksEstablishmentRoute />} />
            <Route path="/risks/phy" element={<RisksFamiliesRoute />} />
            <Route path="/risks/phy/:familyId" element={<RisksFamilyRoute />} />
            <Route path="/risks/rps" element={<RisksRpsRoute />} />
            <Route path="/risks/populations" element={<RisksPopulationsRoute />} />
            <Route path="/risks/populations/:populationId" element={<RisksPopulationRoute />} />
            <Route path="/risks/documents" element={<RisksDocumentRoute />} />
            <Route path="/risks/document/:documentId" element={<RisksDocumentDetailRoute />}>
              {documentRiskRoutes}
            </Route>
            <Route path="/document/:documentId" element={<RisksDocumentDetailRoute />}>
              {documentRiskRoutes}
            </Route>
            <Route path="/risks/scales" element={<RisksScalesRoute />} />
            <Route path="/risks/sites" element={<RisksSitesRoute />} />
            <Route path="/risks/sites/:siteId" element={<RisksSitesRoute />} />
            <Route path="/risks-updates/actions" element={<RisksUpdatesActionsRoute />} />

            <Route path="/learning" element={<LearningRoute />} />

            <Route path="/login/app" element={<LoginApp />} />
            <Route path="/login/code" element={<LoginCode />} />
            <Route path="/login/mail" element={<LoginMail />} />
            <Route path="/login/public" element={<LoginPublic />} />
            <Route path="/login/register" element={<LoginRegister />} />
            <Route path="/login/sso/auth/:org" element={<LoginSsoAuthRoute />} />
            <Route path="/login/sso/redirection" element={<LoginSsoRedirectionRoute />} />
            <Route path="/login/supervisor" element={<LoginSupervisor />} />

            <Route path="/onboarding/video/:video" element={<OnboardingVideo />} />
            <Route path="/onboarding/:step/:index" element={<Onboarding />} />
            <Route path="/onboarding/signup" element={<OnboardingSignup />} />

            <Route path="/project/invitations" element={<ProjectInvitationsRoute />} />
            <Route path="/project/edit" element={<ProjectEditRoute />} />
            <Route path="/project/list" element={<ProjectListRoute />} />

            <Route path="/report/edit" element={<ReportEditRoute />} />
            <Route path="/report/list" element={<ReportListRoute />} />

            <Route path="/superadmin/settings" element={<SuperadminSettings />} />
            <Route path="/superadmin/templates" element={<SuperadminTemplatesRoute />} />
            <Route path="/superadmin/users" element={<SuperadminUsers />} />

            <Route path="/supervisor/account" element={<SupervisorAccount />} />
            <Route path="/supervisor/accounts" element={<SupervisorAccounts />} />
            <Route path="/supervisor/demo-prepare" element={<SupervisorDemoRoute destination="demo-prepare" />} />
            <Route path="/supervisor/demo-account" element={<SupervisorDemoRoute destination="demo-account" />} />
            <Route path="/supervisor/benchmark" element={<SupervisorBenchmarkRoute />} />
            <Route path="/supervisor/feedbacks" element={<SupervisorFeedbacksRoute />} />
            <Route path="/supervisor/lms" element={<SupervisorLms />} />
            <Route path="/supervisor/reports" element={<SupervisorReportsRoute />} />
            <Route path="/supervisor/templates" element={<SupervisorTemplatesRoute />} />

            <Route path="/survey/template" element={<SurveyTemplate />} />
            <Route path="/survey/templates" element={<SurveyTemplates />} />

            <Route path="/tasks/edit" element={<TasksEditRoute />} />
            <Route path="/tasks/all" element={<TasksAllRoute />} />
            <Route path="/tasks/home" element={<TasksHomeRoute />} />
            <Route path="/tasks/self" element={<TasksSelfRoute />} />
            <Route path="/tasks/wizard" element={<TasksWizardRoute />} />

            <Route path="/template/editor" element={<TemplateEditorRoute />} />
            <Route path="/upgrade/:module" element={<UpgradeRoute />} />

            {/** Default redirect to login page **/}
            <Route path="/" element={<Navigate replace to="/login/app" />} />
            <Route path="*" element={<LoginApp />} />
          </Routes>

          <TabletSetupModal />
        </div>
      </BrowserRouter>
    </Suspense>
  )
}

export default App
